"use client"

import { useState, useEffect } from "react"
import { Plus, Trash2, Edit2, Loader2, Database, ImagePlus, UploadCloud, Eye, EyeOff, Image as ImageIcon } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import MediaLibrary from "./media-library"
import { collection, addDoc, getDocs, deleteDoc, updateDoc, doc, serverTimestamp, query, orderBy } from "firebase/firestore"
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { db, storage } from "@/lib/firebase"
import { toast } from "sonner"
import { ensureUniqueSlug } from "@/lib/slugs"

export default function ArticlesManager() {
  const [articles, setArticles] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const [viewState, setViewState] = useState<"list" | "edit">("list")
  const [editingId, setEditingId] = useState<string | null>(null)

  // Form State
  const [title, setTitle] = useState("")
  const [slug, setSlug] = useState("")
  const [category, setCategory] = useState("Current Affairs")
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [content, setContent] = useState("")
  const [portal, setPortal] = useState("all")
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false)

  const fetchArticles = async () => {
    setIsLoading(true)
    try {
      const q = query(collection(db, "articles"), orderBy("createdAt", "desc"))
      const querySnapshot = await getDocs(q)
      setArticles(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))
    } catch (error) {
      console.error(error)
      toast.error("Failed to load articles.")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchArticles()
  }, [])

  const resetForm = () => {
    setTitle("")
    setSlug("")
    setCategory("Current Affairs")
    setImageFile(null)
    setImagePreview(null)
    setContent("")
    setPortal("all")
    setEditingId(null)
    setViewState("list")
  }

  const handleEdit = (article: any) => {
    setTitle(article.title || "")
    setSlug(article.slug || "")
    setCategory(article.category || "Current Affairs")
    setImagePreview(article.image || null)
    setContent(article.content || "")
    setPortal(article.portal || "all")
    setEditingId(article.id)
    setViewState("edit")
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this article?")) return
    try {
      await deleteDoc(doc(db, "articles", id))
      toast.success("Article deleted.")
      fetchArticles()
    } catch (error) {
      toast.error("Failed to delete article.")
    }
  }

  const toggleVisibility = async (article: any) => {
    try {
      const newStatus = !article.isVisible
      await updateDoc(doc(db, "articles", article.id), { isVisible: newStatus })
      toast.success(newStatus ? "Article is now visible" : "Article is now hidden")
      setArticles(prev => prev.map(a => a.id === article.id ? { ...a, isVisible: newStatus } : a))
    } catch (error) {
      toast.error("Failed to update visibility")
    }
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      let finalImageUrl = imagePreview || ""

      if (imageFile) {
        toast.info("Uploading image...")
        const storageRef = ref(storage, `articles/${Date.now()}_${imageFile.name}`)
        await uploadBytes(storageRef, imageFile)
        finalImageUrl = await getDownloadURL(storageRef)
        
        await addDoc(collection(db, "media"), {
           title: title + " Cover",
           url: finalImageUrl,
           type: "image",
           folder: "images",
           createdAt: serverTimestamp(),
        })
      }

      // Generate or validate slug
      const finalSlug = await ensureUniqueSlug("articles", slug || title, editingId || undefined)

      const payload = {
        title,
        slug: finalSlug,
        category,
        image: finalImageUrl,
        content,
        portal,
        updatedAt: serverTimestamp(),
        isVisible: true 
      }

      if (editingId) {
        const existing = articles.find(a => a.id === editingId)
        await updateDoc(doc(db, "articles", editingId), {
          ...payload,
          isVisible: existing?.isVisible !== undefined ? existing.isVisible : true
        })
        toast.success("Article updated.")
      } else {
        await addDoc(collection(db, "articles"), {
          ...payload,
          createdAt: serverTimestamp(),
          views: 0
        })
        toast.success("Article published.")
      }
      resetForm()
      fetchArticles()
    } catch (error) {
      console.error(error)
      toast.error("Failed to save article.")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (viewState === "list") {
    return (
      <div className="bg-white border border-slate-200 shadow-[0_2px_20px_-4px_rgba(0,0,0,0.05)] w-full">
        <div className="p-4 sm:p-8 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-xl sm:text-2xl font-serif font-medium text-slate-950">Article Ledger</h3>
          <button onClick={() => setViewState("edit")} className="bg-slate-950 text-white px-4 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-[#b48c36] transition-colors flex items-center gap-2">
            <Plus size={14} /> Publish Article
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Article</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Category</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Portal</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-8 py-12 text-center">
                    <Loader2 className="animate-spin inline-block text-[#b48c36]" />
                  </td>
                </tr>
              ) : articles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-8 py-12 text-center text-slate-400 text-sm">No articles published yet.</td>
                </tr>
              ) : (
                articles.map((article) => (
                  <tr key={article.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors group">
                    <td className="px-8 py-5">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                          {article.image && <img src={article.image} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div>
                          <p className="font-serif font-bold text-slate-950 leading-none mb-1">{article.title}</p>
                          <p className="text-[10px] text-slate-400 font-mono">/{article.slug || article.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-100 px-2 py-1 rounded">
                        {article.category}
                      </span>
                    </td>
                    <td className="px-8 py-5">
                      <span className={`text-[10px] font-bold uppercase tracking-widest ${article.portal === 'all' ? 'text-blue-500' : 'text-purple-500'}`}>
                        {(!article.portal || article.portal === 'all') ? 'Universal' : article.portal.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-8 py-5">
                      <button 
                        onClick={() => toggleVisibility(article)}
                        className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest ${article.isVisible !== false ? 'text-emerald-500' : 'text-slate-400'}`}
                      >
                        {article.isVisible !== false ? <Eye size={14} /> : <EyeOff size={14} />}
                        {article.isVisible !== false ? 'Live' : 'Hidden'}
                      </button>
                    </td>
                    <td className="px-8 py-5 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleEdit(article)} className="p-2 hover:bg-[#b48c36]/10 text-slate-400 hover:text-[#b48c36] transition-colors rounded">
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleDelete(article.id)} className="p-2 hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors rounded">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white border border-slate-200 shadow-[0_2px_20px_-4px_rgba(0,0,0,0.05)] w-full">
      <div className="p-4 sm:p-8 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-xl sm:text-2xl font-serif font-medium text-slate-950">
          {editingId ? "Modify Article" : "Publish New Article"}
        </h3>
        <button onClick={resetForm} className="text-slate-400 hover:text-slate-950 transition-colors">
          Cancel
        </button>
      </div>

      <form onSubmit={handleSave} className="p-4 sm:p-8 space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Article Title</label>
              <input 
                type="text" 
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="Enter a compelling title..."
                className="w-full bg-slate-50 border border-slate-100 p-4 font-serif text-lg focus:outline-none focus:border-[#b48c36] transition-colors"
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Custom URL Slug (Optional)</label>
              <input 
                type="text" 
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="e.g. latest-exam-updates"
                className="w-full bg-slate-50 border border-slate-100 p-4 font-mono text-sm focus:outline-none focus:border-[#b48c36] transition-colors"
              />
              <p className="text-[9px] text-slate-400 italic">Leave empty to auto-generate from title. Clean URLs improve SEO.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Category</label>
                <select 
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-100 p-4 text-[10px] font-bold uppercase tracking-widest focus:outline-none focus:border-[#b48c36]"
                >
                  <option>Current Affairs</option>
                  <option>TSPSC Updates</option>
                  <option>APPSC Updates</option>
                  <option>General Studies</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Portal Target</label>
                <select 
                  value={portal}
                  onChange={(e) => setPortal(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-100 p-4 text-[10px] font-bold uppercase tracking-widest focus:outline-none focus:border-[#b48c36]"
                >
                  <option value="all">Universal</option>
                  <option value="tspsc">TSPSC Only</option>
                  <option value="appsc">APPSC Only</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Cover Image</label>
              <div className="flex flex-col gap-4">
                <div className="relative group">
                  <div className="w-full h-48 bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="text-slate-200" size={48} />
                    )}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <label className="flex-1 bg-slate-950 text-white p-3 text-[10px] font-bold uppercase tracking-widest text-center cursor-pointer hover:bg-[#b48c36] transition-colors flex items-center justify-center gap-2">
                      <UploadCloud size={14} /> Upload File
                      <input type="file" onChange={handleImageChange} className="hidden" accept="image/*" />
                    </label>
                    <button 
                      type="button"
                      onClick={() => setIsMediaLibraryOpen(true)}
                      className="flex-1 border border-slate-200 p-3 text-[10px] font-bold uppercase tracking-widest hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <Database size={14} /> Media Library
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2 flex flex-col h-full min-h-[400px]">
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Article Content</label>
            <textarea 
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
              placeholder="Write your article here (HTML supported)..."
              className="flex-1 w-full bg-slate-50 border border-slate-100 p-6 font-serif text-slate-800 leading-relaxed focus:outline-none focus:border-[#b48c36] transition-colors resize-none"
            />
          </div>
        </div>

        <div className="pt-8 border-t border-slate-100 flex justify-end gap-4">
          <button 
            type="button"
            onClick={resetForm}
            className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-slate-950 transition-colors"
          >
            Discard
          </button>
          <button 
            type="submit" 
            disabled={isSubmitting}
            className="bg-slate-950 text-white px-12 py-4 text-[10px] font-bold uppercase tracking-widest hover:bg-[#b48c36] transition-all disabled:opacity-50 flex items-center gap-3"
          >
            {isSubmitting ? <Loader2 className="animate-spin" size={14} /> : <Database size={14} />}
            {editingId ? "Update Article" : "Publish Article"}
          </button>
        </div>
      </form>

      {/* Media Library Dialog */}
      <Dialog open={isMediaLibraryOpen} onOpenChange={setIsMediaLibraryOpen}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 overflow-hidden border-none shadow-2xl">
          <DialogHeader className="p-6 bg-slate-950 text-white">
            <DialogTitle className="font-serif text-xl">Institutional Media Assets</DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto">
            <MediaLibrary 
              allowSelection 
              onSelect={(media) => {
                setImagePreview(media.url)
                setImageFile(null)
                setIsMediaLibraryOpen(false)
              }} 
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
